import React, { useEffect, useState } from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import usePrivate from '@afosto/auth-react/usePrivate';
import { useParams, Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import Alert from '@material-ui/lab/Alert';
import Page from '@afosto/app-components-react/Page';
import AllocationSettings from '../../components/AllocationSettings';
import NotificationSettings from '../../components/NotificationSettings';
import PricingSettings from '../../components/PricingSettings';
import RoutesSettings from '../../components/RoutesSettings';
import ServicePointSettings from '../../components/ServicePointSettings';
import TransferSettings from '../../components/TransferSettings';
import VendorSettings from '../../components/VendorSettings';
import { oauth as oauthApi } from '@afosto/iam/api/Oauth';
import translations from './translations';
import ShippingMethodSettings from '../../components/ShippingMethodSettings';
import PaymentMethodSettings from '../../components/PaymentMethodSettings';
import OrderProcessingSettings from '../../components/OrderProcessingSettings';
import ChannelInfoSettings from '../../components/ChannelInfoSettings';
import EmailBCCSettings from '../../components/EmailBCCSettings';
import PosReceiptInfoSettings from '../../components/PosReceiptInfoSettings';
import PaymentCredentialSettings from '../../components/PaymentCredentialSettings';
import ShipmentCredentialSettings from '../../components/ShipmentCredentialSettings';
import SettingsHeader from '../../components/SettingsHeader';

const ViewOauthClient = () => {
  usePrivate();
  const [hasPermissions, UnauthorizedComponent] = usePermissions(['iam:oauth:read']);

  const intl = useIntl();
  const { id } = useParams();
  const [oauthClient, setOauthClient] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchOauthClient = async clientID => {
      setIsLoading(true);

      try {
        const response = await oauthApi.getClient(clientID);
        const { data = {} } = response;

        setOauthClient(data);
      } catch (error) {
        setOauthClient(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOauthClient(id);
  }, [id]);

  if (!hasPermissions()) {
    return (
      <Page title={intl.formatMessage(translations.page.unknownClient.title)}>
        <UnauthorizedComponent />
      </Page>
    );
  }

  return (
    <Page
      title={oauthClient?.name || intl.formatMessage(translations.page.unknownClient.title)}
      isLoading={isLoading}
      breadcrumbs={[
        {
          label: intl.formatMessage(translations.page.breadcrumbs.oauthClients),
          path: '/oauth-clients',
        },
        {
          label: oauthClient?.name,
          path: `/oauth-clients/${oauthClient?.id}`,
          active: true,
        },
      ]}
      linkComponent={Link}
    >
      {!isLoading && !oauthClient?.id && (
        <Alert severity="error">
          <FormattedMessage {...translations.page.unknownClient.message} />
        </Alert>
      )}
      {oauthClient?.id && !isLoading && (
        <>
          <ChannelInfoSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.pos)} />
          <PosReceiptInfoSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.invoicing)} />
          <PricingSettings pathID={id} />
          <VendorSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.messaging)} />
          <NotificationSettings pathID={id} />
          <EmailBCCSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.shipping)} />
          <ShipmentCredentialSettings pathID={id} />
          <ShippingMethodSettings pathID={id} />
          <ServicePointSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.payment)} />
          <PaymentCredentialSettings pathID={id} />
          <PaymentMethodSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.orders)} />
          <OrderProcessingSettings pathID={id} />

          <SettingsHeader title={intl.formatMessage(translations.settings.wms)} />
          <AllocationSettings pathID={id} />
          <RoutesSettings pathID={id} />
          <TransferSettings pathID={id} />
        </>
      )}
    </Page>
  );
};

export default ViewOauthClient;
